import { Mail, Shield } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '@/lib/auth-context'

type LoginStep = 'email' | 'otp'

export function LoginPage(): React.JSX.Element {
  const [step, setStep] = useState<LoginStep>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { login, verifyOtp } = useAuth()
  const navigate = useNavigate()

  async function handleRequestOtp(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)
    try {
      await login(email)
      setStep('otp')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send verification code')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleVerifyOtp(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)
    try {
      await verifyOtp(email, otp)
      navigate('/')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleBack(): void {
    setStep('email')
    setOtp('')
    setError('')
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-admin-950 via-admin-900 to-admin-800 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-admin-500 shadow-lg shadow-admin-500/30">
            <Shield className="h-7 w-7 text-white" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-white">Admin Access Only</h1>
          <p className="mt-1 text-sm text-admin-300">One-Email Internal Dashboard</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl bg-white p-8 shadow-xl">
          {error && (
            <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          {step === 'email' && (
            <EmailStep
              email={email}
              onEmailChange={setEmail}
              onSubmit={handleRequestOtp}
              isSubmitting={isSubmitting}
            />
          )}

          {step === 'otp' && (
            <OtpStep
              email={email}
              otp={otp}
              onOtpChange={setOtp}
              onSubmit={handleVerifyOtp}
              onBack={handleBack}
              isSubmitting={isSubmitting}
            />
          )}
        </div>

        <p className="mt-6 text-center text-xs text-admin-400">
          Access restricted to authorized administrators only.
        </p>
      </div>
    </div>
  )
}

function EmailStep({
  email,
  onEmailChange,
  onSubmit,
  isSubmitting,
}: {
  email: string
  onEmailChange: (v: string) => void
  onSubmit: (e: React.FormEvent) => Promise<void>
  isSubmitting: boolean
}): React.JSX.Element {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      <div>
        <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-gray-700">
          Email address
        </label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder="admin@one-email.dev"
          className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition-colors focus:border-admin-500 focus:ring-2 focus:ring-admin-500/20"
        />
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-xl bg-admin-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-admin-700 disabled:opacity-50"
      >
        {isSubmitting ? 'Sending code...' : 'Send verification code'}
      </button>
    </form>
  )
}

function OtpStep({
  email,
  otp,
  onOtpChange,
  onSubmit,
  onBack,
  isSubmitting,
}: {
  email: string
  otp: string
  onOtpChange: (v: string) => void
  onSubmit: (e: React.FormEvent) => Promise<void>
  onBack: () => void
  isSubmitting: boolean
}): React.JSX.Element {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      <div className="flex flex-col items-center py-2">
        <Mail className="h-10 w-10 text-admin-500" />
        <p className="mt-3 text-sm font-medium text-gray-900">Check your email</p>
        <p className="mt-1 text-center text-sm text-gray-500">
          We sent a 6-digit code to <strong>{email}</strong>
        </p>
      </div>
      <div>
        <label htmlFor="otp" className="mb-1.5 block text-sm font-medium text-gray-700">
          Verification code
        </label>
        <input
          id="otp"
          type="text"
          required
          value={otp}
          onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          maxLength={6}
          className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-center font-mono text-xl tracking-[0.3em] text-gray-900 outline-none transition-colors focus:border-admin-500 focus:ring-2 focus:ring-admin-500/20"
          autoComplete="one-time-code"
        />
      </div>
      <button
        type="submit"
        disabled={isSubmitting || otp.length !== 6}
        className="w-full rounded-xl bg-admin-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-admin-700 disabled:opacity-50"
      >
        {isSubmitting ? 'Verifying...' : 'Verify and sign in'}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-gray-500 transition-colors hover:text-gray-700"
      >
        Use a different email
      </button>
    </form>
  )
}
